import { useQuery } from "@tanstack/react-query";
import { addDays, format, parseISO } from "date-fns";
import { supabase } from "@/lib/supabase";
import {
  buildCommissionReport,
  type CommissionReport,
  type CommissionReportRow,
} from "@/lib/commission-report-calc";

export type {
  CommissionReport,
  CommissionEmployeeSummary,
  CommissionEmployeeProject,
} from "@/lib/commission-report-calc";

/**
 * RPC-en report_commission_by_period er halvåpen [from, to). Konverterer en
 * inklusiv UI-sluttdato til eksklusiv øvre grense (dagen etter), ellers
 * droppes prosjekter låst nøyaktig på sluttdatoen (f.eks. 31.12). Delt av
 * provisjon-resolveren og CSV-eksporten så grensen aldri drifter mellom dem.
 */
export function commissionPeriodToExclusive(period: { from: string; to: string }): {
  from: string;
  to: string;
} {
  return { from: period.from, to: format(addDays(parseISO(period.to), 1), "yyyy-MM-dd") };
}

/**
 * Non-hook data fetch for provisjonsrapporten. Kaller RPC
 * report_commission_by_period og bygger utbetalingsgrunnlaget via den delte
 * buildCommissionReport. Eksportert så rapport-resolvere kan gjenbruke samme
 * query uten å duplisere logikk (resolvere er ikke React-hooks).
 */
export async function fetchCommissionReport(period: {
  from: string;
  to: string;
}): Promise<CommissionReport> {
  const { data, error } = await supabase.rpc("report_commission_by_period", {
    p_from: period.from,
    p_to: period.to,
  });
  if (error) throw error;
  return buildCommissionReport((data ?? []) as CommissionReportRow[]);
}

/**
 * Provisjonsrapport per ansatt for en periode. RPC report_commission_by_period
 * gjør join/filter + admin-sjekk server-side; her bygges utbetalingsgrunnlaget
 * via den delte buildCommissionReport (gjenbruker calcCommissionSplits).
 */
export function useCommissionReport(period: { from: string; to: string }) {
  return useQuery({
    queryKey: ["commission-report", { from: period.from, to: period.to }],
    staleTime: 5 * 60 * 1000,
    queryFn: (): Promise<CommissionReport> => fetchCommissionReport(period),
  });
}
